import { i18n } from 'next-i18next';

import { translationApiInstance } from '@kc-console/context';

import { DEFAULT_USER_CONFIG, SUPPORTED_LANGUAGES } from '../constants';
import { isTranslation } from '../types';
import { getCurrentLanguage } from './getCurrentLanguage';
import { getTranslationsFromFallback } from './getTranslationsFromFallback';
import { getServerTraceHttpTimingOptions, recordServerTraceSpanMeta } from './serverTrace/store';

import type { SSRConfig } from 'next-i18next';
import type { Resource } from 'i18next';
import type {
  BuildInitialI18nStoreProps,
  SyncI18nResourcesProps,
  SyncI18nResourcesWithStateProps,
  TranslationMap,
} from '../types';

/**
 * initialI18nStore 생성. 이번 요청에서 fetch한 translationMap만 사용한다.
 * 서버 싱글톤 i18n의 누적 상태에 의존하면 배포 직후 stale 번역이 노출될 수 있으므로 참조하지 않는다.
 */
const buildInitialI18nStore = ({
  languages,
  namespaces,
  translationMap,
}: BuildInitialI18nStoreProps): Resource => {
  const store: Resource = {};

  for (const lang of languages) {
    store[lang] = {};

    for (const ns of namespaces) {
      const fromMap = translationMap.get(ns)?.[lang];
      if (fromMap && isTranslation(fromMap)) {
        store[lang][ns] = fromMap;
      }
    }
  }

  return store;
};

const resolveLocale = async (props: SyncI18nResourcesProps): Promise<string> => {
  if (props.locale) return props.locale;

  const { queryClient, state, context } = props as SyncI18nResourcesWithStateProps;
  const { language } = await getCurrentLanguage({ queryClient, state, context });
  return language;
};

/**
 * fallback 경로에서 i18n 스토어에 주입된 번역을 translationMap에 반영합니다.
 * buildInitialI18nStore가 translationMap만 참조하므로, fallback 결과를 initialI18nStore에 전달하려면 필요합니다.
 */
const mergeFallbackIntoTranslationMap = (
  translationMap: TranslationMap,
  languages: string[],
  namespaces: string[],
) => {
  for (const ns of namespaces) {
    const nsMap = translationMap.get(ns) ?? {};

    for (const lang of languages) {
      if (nsMap[lang]) continue;

      const fromI18n = i18n?.getResourceBundle(lang, ns);
      if (fromI18n && isTranslation(fromI18n)) {
        nsMap[lang] = fromI18n;
      }
    }

    if (Object.keys(nsMap).length > 0) {
      translationMap.set(ns, nsMap);
    }
  }
};

/**
 * 서버 사이드에서 페이지 렌더링에 필요한 다국어 리소스를 준비합니다.
 * @description
 * - 매 SSR 요청마다 translation API에서 최신 번역을 가져옵니다
 * - translation-api가 배포 시점에 Redis 캐시를 무효화하므로 다국어 배포 즉시 반영됩니다
 * - API 실패 시 로컬 fallback으로 로드합니다
 * - dehydratedState 중복 직렬화를 피하기 위해 queryClient 캐시 없이 직접 호출합니다
 */
export const syncI18nResources = async (props: SyncI18nResourcesProps): Promise<SSRConfig> => {
  const { namespace, languages = SUPPORTED_LANGUAGES, context, guard } = props;

  const translationMap: TranslationMap = new Map();
  const discoveredNamespaces = new Set<string>([namespace]);
  const languagesParam = languages.join(',');

  try {
    const { fetchTranslations } = translationApiInstance({
      ...(context ? getServerTraceHttpTimingOptions(context.req, 'translation', guard) : undefined),
    });

    const apiStartedAt = Date.now();
    const translationItems =
      (await fetchTranslations({ languages: languagesParam, keywords: namespace })) || [];
    const apiMs = Date.now() - apiStartedAt;

    if (context && guard) {
      recordServerTraceSpanMeta(context.req, guard, {
        namespace,
        apiMs,
        outcome: 'api',
        itemCount: translationItems.length,
        languages: languagesParam,
      });
    }

    for (const item of translationItems) {
      const ns = item.keyword;
      discoveredNamespaces.add(ns);
      translationMap.set(ns, item.translations);

      for (const lang of languages) {
        const translation = item.translations[lang];

        if (!isTranslation(translation)) {
          console.error(`[i18n VALIDATION ERROR] Invalid translation data for "${ns}" (${lang})`);
          continue;
        }

        i18n?.addResourceBundle(lang, ns, translation, true, true);
      }
    }

    return {
      _nextI18Next: {
        initialI18nStore: buildInitialI18nStore({
          languages,
          namespaces: Array.from(discoveredNamespaces),
          translationMap,
        }),
        initialLocale: await resolveLocale(props),
        ns: [namespace],
        userConfig: DEFAULT_USER_CONFIG,
      },
    };
  } catch (error) {
    console.error(`[i18n API ERROR] Failed to fetch from API:`, error);

    const fallbackResults = await getTranslationsFromFallback([namespace], languages);

    if (context && guard) {
      recordServerTraceSpanMeta(context.req, guard, {
        namespace,
        languages: languagesParam,
        outcome: fallbackResults.successCount === 0 ? 'error' : 'fallback',
        itemCount: fallbackResults.successCount,
      });
    }

    if (fallbackResults.successCount === 0) {
      console.error('[i18n FALLBACK ERROR] All fallback requests failed');
      throw error;
    }

    mergeFallbackIntoTranslationMap(translationMap, languages, Array.from(discoveredNamespaces));

    return {
      _nextI18Next: {
        initialI18nStore: buildInitialI18nStore({
          languages,
          namespaces: Array.from(discoveredNamespaces),
          translationMap,
        }),
        initialLocale: await resolveLocale(props),
        ns: [namespace],
        userConfig: DEFAULT_USER_CONFIG,
      },
    };
  }
};
